import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Box, Button, Group, Segmented, Stack, Text, TextInput, Title } from '@grovus/design-system'
import {
  createShareLink,
  LABEL_MAX,
  listShareLinks,
  revokeShareLink,
  SHARE_DAYS,
  shareUrl,
  type ShareDays,
  type ShareLink,
} from '../data/shareLinks'
import type { Outcome } from '../data/members'
import { SHARE_COPY, type Lang } from '../i18n'
import { fmt } from '../lib/format'
import { shareDate } from '../share/date'

const BORDER = '1px solid var(--mantine-color-default-border)'

interface Props {
  lang: Lang
  companyId: string
  companyName: string
  /** A `404` or `403`: the caller's access or role changed under the dialog. */
  onAccessLost: (companyId: string) => void
  onClose: () => void
}

type ListState =
  | { phase: 'loading' }
  | { phase: 'ready'; links: ShareLink[] }
  | { phase: 'failed' }

/**
 * "Share read-only" (GRO-83): create a link, show it once with Copy, and the company's live
 * links with an inline Revoke. Follows the invite form's layout. `503 share_links_not_open`
 * replaces the create form with the not-open line, so the entry stays honest between the
 * release that ships the routes and the one that raises the floor.
 */
export function ShareDialog({ lang, companyId, companyName, onAccessLost, onClose }: Props) {
  const copy = SHARE_COPY[lang]
  const [days, setDays] = useState<ShareDays>(7)
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState<string | null>(null)
  const [notOpen, setNotOpen] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [list, setList] = useState<ListState>({ phase: 'loading' })
  const [confirming, setConfirming] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [revokeFailed, setRevokeFailed] = useState<string | null>(null)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  /** The answers every call shares; returns whether the caller still handles the outcome. */
  const settled = useCallback(<T,>(outcome: Outcome<T>): boolean => {
    if (outcome.status === 'stale' || outcome.status === 'expired') return false
    if (outcome.status === 'notFound' || outcome.status === 'forbidden') {
      onAccessLost(companyId)
      return false
    }
    return true
  }, [companyId, onAccessLost])

  const load = useCallback(async () => {
    setList({ phase: 'loading' })
    const outcome = await listShareLinks(companyId)
    if (!alive.current || !settled(outcome)) return
    setList(outcome.status === 'ok' ? { phase: 'ready', links: outcome.body } : { phase: 'failed' })
  }, [companyId, settled])
  useEffect(() => { void load() }, [load])

  async function create(event: FormEvent) {
    event.preventDefault()
    const trimmed = label.trim()
    if ([...trimmed].length > LABEL_MAX) {
      setLine(copy.invalidLabel)
      return
    }
    setBusy(true)
    setLine(null)
    const outcome = await createShareLink(companyId, days, trimmed === '' ? null : trimmed)
    if (!alive.current) return
    setBusy(false)
    if (!settled(outcome)) return
    if (outcome.status === 'ok') {
      setToken(outcome.body.token)
      setCopied(false)
      setLabel('')
      setList((prev) => prev.phase === 'ready' ? { phase: 'ready', links: [outcome.body.link, ...prev.links] } : prev)
      return
    }
    if (outcome.status === 'refused' && outcome.code === 'share_links_not_open') setNotOpen(true)
    else if (outcome.status === 'refused' && outcome.code === 'invalid_label') setLine(copy.invalidLabel)
    else setLine(copy.failed)
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link)
      if (alive.current) setCopied(true)
    } catch {
      if (alive.current) setCopied(false)
    }
  }

  async function revoke(id: string) {
    setRevoking(id)
    setRevokeFailed(null)
    const outcome = await revokeShareLink(companyId, id)
    if (!alive.current) return
    setRevoking(null)
    if (outcome.status === 'notFound') {
      // A link that is already gone leaves the list either way.
      setConfirming(null)
      void load()
      return
    }
    if (!settled(outcome)) return
    if (outcome.status === 'ok') {
      setConfirming(null)
      setList((prev) => prev.phase === 'ready' ? { phase: 'ready', links: prev.links.filter((l) => l.id !== id) } : prev)
    } else {
      setRevokeFailed(id)
    }
  }

  const link = token === null ? null : shareUrl(token)

  return (
    <Stack gap={16} maw={720} data-testid="share-dialog">
      <Group justify="space-between" align="baseline">
        <Title order={2} fz="lg">{copy.share}: {companyName}</Title>
        <Button variant="default" size="compact-sm" onClick={onClose}>{copy.close}</Button>
      </Group>
      <Text fz="sm">{copy.lede}</Text>

      {notOpen ? (
        <Text role="status" data-testid="share-not-open">{copy.notOpen}</Text>
      ) : (
        <Box p={16} style={{ border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }}>
          <form onSubmit={(event) => { void create(event) }} noValidate>
            <Stack gap={12}>
              <Box>
                <Text fz="sm" fw={500} pb={4} id="share-expiry">{copy.expiry}</Text>
                <Segmented
                  aria-labelledby="share-expiry"
                  value={String(days)}
                  onChange={(value) => setDays(Number(value) as ShareDays)}
                  data={SHARE_DAYS.map((d) => ({ value: String(d), label: copy.days[d] }))}
                />
              </Box>
              <TextInput
                label={copy.label}
                description={copy.labelHint}
                value={label}
                onChange={(event) => setLabel(event.currentTarget.value)}
                autoComplete="off"
              />
              {line !== null && <Text fz="sm" role="alert">{line}</Text>}
              <Group><Button type="submit" disabled={busy}>{copy.create}</Button></Group>
            </Stack>
          </form>
        </Box>
      )}

      {link !== null && (
        <Stack gap={12} p={16} style={{ border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }} data-testid="share-result">
          <Group align="flex-end" gap="12px 16px" wrap="wrap">
            <TextInput label={copy.linkLabel} value={link} readOnly style={{ flex: '1 1 280px' }} />
            <Button variant="default" onClick={() => { void copyLink(link) }}>{copy.copy}</Button>
          </Group>
          {copied && <Text fz="sm" role="status">{copy.copied}</Text>}
          <Text fz="sm" className="grv-muted">{copy.shownOnce}</Text>
        </Stack>
      )}

      <Box>
        <Title order={3} fz="md" pb={8}>{copy.live}</Title>
        {list.phase === 'loading' && <Text fz="sm" className="grv-muted">{copy.loading}</Text>}
        {list.phase === 'failed' && (
          <Group gap="xs" role="alert">
            <Text fz="sm">{copy.loadFailed}</Text>
            <Button size="compact-sm" variant="default" onClick={() => { void load() }}>{copy.retry}</Button>
          </Group>
        )}
        {list.phase === 'ready' && list.links.length === 0 && <Text fz="sm" className="grv-muted">{copy.none}</Text>}
        {list.phase === 'ready' && list.links.length > 0 && (
          <Stack gap={8} component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {list.links.map((row) => (
              <Box component="li" key={row.id} p={12} style={{ border: BORDER, borderRadius: 'var(--mantine-radius-md)' }} data-testid="share-link-row">
                <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
                  <Box style={{ minWidth: 0 }}>
                    <Text fw={500} style={{ overflowWrap: 'anywhere' }}>{row.label ?? copy.noLabel}</Text>
                    <Text fz="sm" className="grv-muted" style={{ overflowWrap: 'anywhere' }}>
                      {fmt(copy.createdBy, { email: row.createdBy })}
                      {' · '}
                      {fmt(copy.expires, { date: shareDate(row.expiresAt, lang) })}
                      {' · '}
                      {row.lastOpenedAt === null ? copy.notOpened : fmt(copy.lastOpened, { date: shareDate(row.lastOpenedAt, lang) })}
                    </Text>
                  </Box>
                  {confirming !== row.id && (
                    <Button variant="subtle" size="compact-sm" c="bad.6" onClick={() => { setConfirming(row.id); setRevokeFailed(null) }}>
                      {copy.revoke}
                    </Button>
                  )}
                </Group>
                {confirming === row.id && (
                  <Box pt={8} data-testid="revoke-confirm">
                    <Text fz="sm">{copy.confirmRevoke}</Text>
                    {revokeFailed === row.id && <Text fz="sm" pt={4} role="alert">{copy.failed}</Text>}
                    <Group gap="xs" pt="xs">
                      <Button
                        variant="default"
                        size="compact-sm"
                        c="bad.6"
                        loading={revoking === row.id}
                        onClick={() => { void revoke(row.id) }}
                      >
                        {copy.revoke}
                      </Button>
                      <Button variant="default" size="compact-sm" disabled={revoking === row.id} onClick={() => setConfirming(null)}>
                        {copy.cancel}
                      </Button>
                    </Group>
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  )
}
